// Redes sociales de la firma del PDF. Misma regla que el backend
// (cotizador-dv-backend/src/services/redesSociales.js, que es quien decide al
// guardar) y que la plantilla del PDF en n8n, que vuelve a validar: solo
// https y el dominio de cada red. Aquí sirve para avisar mientras escribe.
// Los íconos son los mismos trazos que imprime el PDF.

export type NombreRed =
  | "instagram"
  | "facebook"
  | "tiktok"
  | "linkedin"
  | "youtube"
  | "x"
  | "whatsapp"
  | "web";
export interface Red {
  red: NombreRed;
  url: string;
}

export const REDES: Record<
  NombreRed,
  { nombre: string; dominios: string[] | null; ejemplo: string; icono: string }
> = {
  instagram: {
    nombre: "Instagram",
    dominios: ["instagram.com"],
    ejemplo: "instagram.com/diezvisual",
    icono:
      '<rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>',
  },
  facebook: {
    nombre: "Facebook",
    dominios: ["facebook.com", "fb.com"],
    ejemplo: "facebook.com/diezvisual",
    icono: '<path d="M14 8h3V4h-3a4 4 0 0 0-4 4v3H7v4h3v6h4v-6h3l1-4h-4z"/>',
  },
  tiktok: {
    nombre: "TikTok",
    dominios: ["tiktok.com"],
    ejemplo: "tiktok.com/@diezvisual",
    icono:
      '<path d="M14 3v11a3.5 3.5 0 1 1-3.5-3.5"/><path d="M14 3c.5 2.5 2.5 4.5 5 5"/>',
  },
  linkedin: {
    nombre: "LinkedIn",
    dominios: ["linkedin.com"],
    ejemplo: "linkedin.com/in/tu-nombre",
    icono:
      '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 10v7M8 7v.01M12 17v-4a2 2 0 0 1 4 0v4M12 10v7"/>',
  },
  youtube: {
    nombre: "YouTube",
    dominios: ["youtube.com", "youtu.be"],
    ejemplo: "youtube.com/@diezvisual",
    icono:
      '<rect x="2" y="5" width="20" height="14" rx="4"/><path d="m10 9 5 3-5 3z" fill="currentColor"/>',
  },
  x: {
    nombre: "X (Twitter)",
    dominios: ["x.com", "twitter.com"],
    ejemplo: "x.com/diezvisual",
    icono: '<path d="M4 4l16 16M20 4 4 20"/>',
  },
  whatsapp: {
    nombre: "WhatsApp",
    dominios: ["wa.me", "whatsapp.com", "wa.link"],
    ejemplo: "wa.me/593991234567",
    icono:
      '<path d="M4 20l1.3-4A8 8 0 1 1 8 18.7z"/><path d="M9 9.5c0 3 2.5 5.5 5.5 5.5l1-1.5-2-1-1 1a4 4 0 0 1-2-2l1-1-1-2z"/>',
  },
  web: {
    nombre: "Sitio web",
    dominios: null,
    ejemplo: "diezvisual.ec",
    icono:
      '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/>',
  },
};
export const ORDEN_REDES = Object.keys(REDES) as NombreRed[];

// "instagram.com/diez" → "https://instagram.com/diez"; null si no sirve.
export function normalizarUrl(texto: string): string | null {
  let t = texto.trim();
  if (!t) return null;
  if (!/^[a-z][a-z0-9+.-]*:/i.test(t)) t = `https://${t}`;
  t = t.replace(/^http:\/\//i, "https://");
  let url: URL;
  try {
    url = new URL(t);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" || url.username || url.password) return null;
  if (!url.hostname.includes(".")) return null;
  const limpio = url.toString();
  return limpio.length <= 300 ? limpio : null;
}

// Mensaje de error para mostrar junto al campo, o null si está bien (o vacío).
export function errorDeRed(red: NombreRed, texto: string): string | null {
  if (!texto.trim()) return null;
  const url = normalizarUrl(texto);
  if (!url) return "Enlace no válido.";
  const dominios = REDES[red].dominios;
  const host = new URL(url).hostname.toLowerCase();
  if (dominios && !dominios.some((d) => host === d || host.endsWith(`.${d}`)))
    return `Tiene que ser un enlace de ${dominios.join(" o ")}.`;
  return null;
}

// Texto junto al ícono en el PDF: @usuario en redes, el dominio en la web.
export function textoRed(red: NombreRed, url: string): string {
  const u = new URL(url);
  if (red === "web") return u.hostname.replace(/^www\./, "");
  if (red === "whatsapp") return "WhatsApp";
  const partes = u.pathname
    .split("/")
    .filter(Boolean)
    .filter((p) => !["in", "company", "c", "user", "@"].includes(p));
  let usuario = (partes[0] ?? "").replace(/^@/, "");
  try {
    usuario = decodeURIComponent(usuario);
  } catch {
    usuario = "";
  }
  return /^[\w.-]{1,40}$/.test(usuario)
    ? `@${usuario}`
    : u.hostname.replace(/^www\./, "");
}
